import { DB_TYPE } from '@/common/define/common.define';
import {
  getGetV7ClickhouseSqlTextByInstanceIdAndNormalizedQueryHashQueryKey,
  getV7ClickhouseSqlTextByInstanceIdAndNormalizedQueryHash,
} from '@/api-client/clickhouse/queries/clickhouse-controller/clickhouse-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import type { DocumentTextParams, ParameterDetailParams, SqlDetailParams } from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  sqlDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SqlDetailSchema,
  type ClickhouseSqlDetailAddition,
  type ParameterDetailSchema,
  type ClickhouseParameterDetailAddition,
  type DocumentTextSchema,
} from './clickhouse.schema';

export const getClickhouseSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: ClickhouseSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: formatName(data.normalizedQueryHash),
    addition: {
      dbType: DB_TYPE.CLICKHOUSE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      normalizedQueryHash: data.normalizedQueryHash,
      xmSqlId: data.xmSqlId,
      user: data.user,
      currentDatabase: data.currentDatabase,
      hostname: data.hostname,
    },
  };
};

export const getClickhouseParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: ClickhouseParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.CLICKHOUSE,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getClickhouseDocumentTextParams = ({
  detailInfo,
  instanceId,
  useFieldValidation,
}: DocumentTextParams) => {
  if (!useFieldValidation) {
    return {
      instanceId,
      normalizedQueryHash: detailInfo.normalizedQueryHash,
      xmSqlId: detailInfo.xmSqlId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    normalizedQueryHash: data.normalizedQueryHash,
    xmSqlId: data.xmSqlId,
  };
};

export const fetchClickhouseDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getClickhouseDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7ClickhouseSqlTextByInstanceIdAndNormalizedQueryHash(
    params.instanceId!,
    params.normalizedQueryHash,
    { xmSqlId: params.xmSqlId },
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0]?.sqlFullText || null;
};

export const getClickhouseDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7ClickhouseSqlTextByInstanceIdAndNormalizedQueryHashQueryKey();
  }

  const convertedParams = getClickhouseDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7ClickhouseSqlTextByInstanceIdAndNormalizedQueryHashQueryKey(
    convertedParams.instanceId,
    convertedParams.normalizedQueryHash,
    convertedParams,
  );
};

export const clickhouseSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const clickhouseDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
